import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import {
  SortDescriptor,
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import ReactDOM from "react-dom";

interface AiSession {
  id: number;
  anonId: string | null;
  userId: number | null;
  threadId: string | null;
  threadName: string;
  messageCount: number;
  createdOn: string;
  lastModifiedOn: string;
  email: string;
  fromIP: string;
  isDeleted: boolean;
  cloudflarePassed: boolean | null;
  acctId: string | null;
  userDisplay: string | null;
}

interface FilterState {
  email: string;
  fromIP: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  isDeleted: boolean | null;
  hasReportedInteractions: boolean;
  mode: "" | "voice" | "text";
}

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

const AISessions: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<AiSession[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ skip: 0, take: 50 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "id", dir: "desc" },
  ]);
  const [gridFilter, setGridFilter] = useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [filters, setFilters] = useState<FilterState>({
    email: "",
    fromIP: "",
    dateFrom: null,
    dateTo: null,
    isDeleted: null,
    hasReportedInteractions: false,
    mode: "",
  });
  const gridRef = useRef<HTMLDivElement>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const currentSort = sort.length > 0 ? sort[0] : null;
      const requestBody = {
        email: filters.email || null,
        fromIP: filters.fromIP || null,
        dateFrom: filters.dateFrom?.toISOString() || null,
        dateTo: filters.dateTo?.toISOString() || null,
        isDeleted: filters.isDeleted,
        hasReportedInteractions: filters.hasReportedInteractions || null,
        mode: filters.mode || null,
        skip: page.skip,
        take: page.take,
        sortField: currentSort?.field || "lastModifiedOn",
        sortDir: currentSort?.dir || "desc",
      };

      const response = await callApi(
        "sysadmin/ai/sessions",
        HttpMethod.POST,
        requestBody,
      );
      setSessions(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error("Error fetching AI sessions:", err);
      setError(`Error fetching AI sessions: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, sort]);

  const handleSearch = () => {
    setPage({ skip: 0, take: page.take });
    loadData();
  };

  const handleReset = () => {
    setFilters({
      email: "",
      fromIP: "",
      dateFrom: null,
      dateTo: null,
      isDeleted: null,
      hasReportedInteractions: false,
      mode: "",
    });
    setPage({ skip: 0, take: 50 });
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleGridFilterChange = (event: GridFilterChangeEvent) => {
    setGridFilter(event.filter);
  };

  const handleRowClick = (dataItem: AiSession) => {
    navigate(`/sysadmin/ai/sessions/${dataItem.id}`);
  };

  // Custom cell for clickable row
  const ClickableCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : "";
    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{ cursor: "pointer" }}
      >
        {value?.toString() || "-"}
      </td>
    );
  };

  // Custom cell for date formatting
  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : null;
    const formatted = value
      ? new Date(value).toLocaleString("en-US", {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "-";
    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{ cursor: "pointer" }}
      >
        {formatted}
      </td>
    );
  };

  // Custom cell for boolean display
  const BooleanCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : null;
    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{ cursor: "pointer" }}
      >
        {value === true ? "Yes" : value === false ? "No" : "-"}
      </td>
    );
  };

  return (
    <div className="container-fluid p-3">
      <h4 className="mb-3">AI Sessions</h4>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          {/* Six equal-width filters so the row stays visually balanced */}
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Email</label>
              <input
                type="text"
                className="form-control"
                value={filters.email}
                onChange={(e) =>
                  setFilters({ ...filters, email: e.target.value })
                }
                placeholder="Filter by email..."
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">IP Address</label>
              <input
                type="text"
                className="form-control"
                value={filters.fromIP}
                onChange={(e) =>
                  setFilters({ ...filters, fromIP: e.target.value })
                }
                placeholder="Filter by IP..."
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Date From</label>
              <DatePicker
                selected={filters.dateFrom}
                onChange={(date) => setFilters({ ...filters, dateFrom: date })}
                dateFormat="yyyy-MM-dd"
                isClearable
                className="form-control"
                wrapperClassName="w-100"
                placeholderText="Start date"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Date To</label>
              <DatePicker
                selected={filters.dateTo}
                onChange={(date) => setFilters({ ...filters, dateTo: date })}
                dateFormat="yyyy-MM-dd"
                isClearable
                className="form-control"
                wrapperClassName="w-100"
                placeholderText="End date"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Mode</label>
              <select
                className="form-select"
                value={filters.mode}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    mode: e.target.value as FilterState["mode"],
                  })
                }
              >
                <option value="">All</option>
                <option value="voice">🎤 Voice</option>
                <option value="text">💬 Text</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Deleted</label>
              <select
                className="form-select"
                value={
                  filters.isDeleted === null
                    ? ""
                    : filters.isDeleted
                      ? "true"
                      : "false"
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    isDeleted:
                      e.target.value === "" ? null : e.target.value === "true",
                  })
                }
              >
                <option value="">All</option>
                <option value="false">Active Only</option>
                <option value="true">Deleted Only</option>
              </select>
            </div>
          </div>
          <div className="d-flex align-items-center mt-3">
            <button className="btn btn-primary me-2" onClick={handleSearch}>
              Search
            </button>
            <button className="btn btn-secondary me-4" onClick={handleReset}>
              Reset
            </button>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="hasReported"
                checked={filters.hasReportedInteractions}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    hasReportedInteractions: e.target.checked,
                  })
                }
              />
              <label className="form-check-label" htmlFor="hasReported">
                Reported only
              </label>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={gridFilter ? filterBy(sessions, gridFilter) : sessions}
          total={gridFilter ? filterBy(sessions, gridFilter).length : total}
          pageable={{
            buttonCount: 5,
            pageSizes: [25, 50, 100],
          }}
          skip={page.skip}
          take={page.take}
          onPageChange={handlePageChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          filterable={true}
          filter={gridFilter}
          onFilterChange={handleGridFilterChange}
          resizable={true}
          style={{ height: "calc(100vh - 350px)" }}
        >
          <Column
            field="id"
            title="ID"
            width={80}
            cells={{ data: ClickableCell }}
            filter="numeric"
          />
          <Column
            field="email"
            title="Email"
            width={200}
            cells={{ data: ClickableCell }}
            filter="text"
          />
          <Column
            field="userDisplay"
            title="User"
            width={200}
            cells={{ data: ClickableCell }}
            filter="text"
          />
          <Column
            field="threadName"
            title="Thread Name"
            width={200}
            cells={{ data: ClickableCell }}
            filter="text"
          />
          <Column
            field="messageCount"
            title="Messages"
            width={100}
            cells={{ data: ClickableCell }}
            filter="numeric"
          />
          <Column
            field="fromIP"
            title="IP Address"
            width={130}
            cells={{ data: ClickableCell }}
            filter="text"
          />
          <Column
            field="createdOn"
            title="Created"
            width={150}
            cells={{ data: DateCell }}
            filter="date"
          />
          <Column
            field="lastModifiedOn"
            title="Last Modified"
            width={150}
            cells={{ data: DateCell }}
            filter="date"
          />
          <Column
            field="isDeleted"
            title="Deleted"
            width={80}
            cells={{ data: BooleanCell }}
            filter="boolean"
          />
          <Column
            field="cloudflarePassed"
            title="CF Passed"
            width={100}
            cells={{ data: BooleanCell }}
            filter="boolean"
          />
          <Column
            field="acctId"
            title="Acct ID"
            width={120}
            cells={{ data: ClickableCell }}
            filter="text"
          />
        </Grid>
      </div>

      <div className="mt-3">
        <small className="text-muted">
          Click on a row to view session interactions
        </small>
      </div>
    </div>
  );
};

export default AISessions;
